import * as amplitude from '@amplitude/analytics-browser';
import usePrevious from '@liveauctioneers/caterwaul-components/lib/hooks/usePrevious';
import { useEffect } from 'react';

import { useCatalogPagePropertiesPlugin } from '@/components/Analytics/Plugins/CatalogPagePropertiesPlugin/CatalogPagePropertiesPlugin';
import { useItemPagePropertiesPlugin } from '@/components/Analytics/Plugins/ItemPagePropertiesPlugin/ItemPagePropertiesPlugin';
import { UserPropertiesPlugin } from '@/components/Analytics/Plugins/UserPropertiesPlugin/UserPropertiesPlugin';
import { useAppSelector } from '@/redux/hooks';
import {
    getBidderHasEverPlacedBid,
    getBidderHasEverRegisteredForAuction,
    getBidderId,
    getUserCreationDate,
    isUserLoggedIn,
} from '@/redux/modules/account/user/user.selectors';
import { useAmplitudeExperiments } from '@/utils/amplitude/experiments/utils/useAmplitudeExperiments';

const AmplitudeProvider = () => {
    const bidderId = useAppSelector(getBidderId);
    const accountCreationDate = useAppSelector(getUserCreationDate);
    const isLoggedIn = useAppSelector(isUserLoggedIn);
    const wasLoggedIn = usePrevious(isLoggedIn);
    const { client, setVariants } = useAmplitudeExperiments();

    const hasEverRegisteredForAuction = useAppSelector(getBidderHasEverRegisteredForAuction);
    const hasEverPlacedBid = useAppSelector(getBidderHasEverPlacedBid);

    const fetchAmplitudeExperimentsOnIdentityChange = async () => {
        client.setUser({
            device_id: amplitude.getDeviceId(),
            user_id: isLoggedIn ? String(bidderId) : undefined,
            user_properties: {
                'Has Ever Placed Bid': hasEverPlacedBid,
                'Has Ever Registered For Auction': hasEverRegisteredForAuction,
            },
        });

        const newClient = await client.fetch({
            device_id: amplitude.getDeviceId(),
            user_id: isLoggedIn ? String(bidderId) : undefined,
            user_properties: {
                'Has Ever Placed Bid': hasEverPlacedBid,
                'Has Ever Registered For Auction': hasEverRegisteredForAuction,
            },
        });
        const newVariants = newClient.all();
        setVariants(newVariants);
    };

    // identify user
    useEffect(
        () => {
            // if the user is logged in and there are no plugins registered, we add the information to amplitude now
            if (isLoggedIn && !wasLoggedIn) {
                // Set userId synchronously - amplitude.add() processes plugins async, so setup()
                // may run after events fire. Without this, re-login without refresh sends events with null userId.
                amplitude.setUserId(String(bidderId));
                const userPropertiesPlugin = new UserPropertiesPlugin();
                userPropertiesPlugin.addBidderInfo({
                    accountCreationDate,
                    bidderId,
                    hasEverPlacedBid,
                    hasEverRegisteredForAuction,
                });
                amplitude.add(userPropertiesPlugin);
                fetchAmplitudeExperimentsOnIdentityChange();
            } else if (!isLoggedIn) {
                amplitude.setUserId(null);
                // It seems like exposure tracking doesn't fully trigger on logged out users unless we call fetch here
                fetchAmplitudeExperimentsOnIdentityChange();
            }
        },
        // We don't want to watch the "plugins" array, because it gets changed in here and can cause an infinite loop of forcing this useEffect to be called
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [accountCreationDate, bidderId, isLoggedIn]
    );

    useItemPagePropertiesPlugin();
    useCatalogPagePropertiesPlugin();

    return null;
};

export default AmplitudeProvider;
